import { Fragment, useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, TextField } from "@shapr3d/frontend-lib";
import { useForm } from "react-hook-form";
import { z } from "zod";

import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import useTranslation from "../../hooks/useTranslation";
import {
  createUserNameSchemaWithTranslations,
  MAX_NAME_LENGTH,
  useUpdateName,
} from "../../hooks/useUpdateName";
import { PageSource } from "../../types/tracking";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";

import style from "./UpdateName.module.css";

type UpdateNameFormData = {
  name: string;
};

export const UpdateName = () => {
  const { data: accountData, isLoading: isAccountDataLoading } = useAccount();
  const { t, m } = useTranslation();

  const schema = useMemo(
    () =>
      z.object({
        name: createUserNameSchemaWithTranslations(t),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    setError,
    trigger,
    setValue,
    watch,
    formState: { errors },
  } = useForm<UpdateNameFormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: accountData?.name ?? "" },
    delayError: 600,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const navigation = useNavigation();
  const { mutateAsync, isPending } = useUpdateName({
    onSuccess: () => {
      if (!navigation.tryRedirectFromSearchParams(PageSource.Signin)) {
        navigation.goToProfile();
      }
    },
    onError: () => {
      setError("name", { message: t(m.ErrorMessage.Unknown) });
    },
  });

  const onSubmit = async (values: UpdateNameFormData) => {
    try {
      await mutateAsync(values);
    } catch {
      // Error is already handled by onError callback
    }
  };

  const userName = watch("name");

  useEffect(() => {
    if (accountData?.name && !isAccountDataLoading) {
      setValue("name", accountData.name);
      trigger("name");
    }
  }, [accountData?.name, trigger, isAccountDataLoading, setValue]);

  const formTitle = useMemo(() => {
    if (accountData?.name) {
      return t(m.UpdateName.TitleExisting);
    }
    return t(m.UpdateName.TitleNew);
  }, [accountData?.name, m, t]);

  const descriptionLines = useMemo(() => {
    if (accountData?.name) {
      return t(m.UpdateName.DescriptionExisting).split("\n");
    }

    return t(m.UpdateName.DescriptionEmpty).split("\n");
  }, [accountData?.name, m, t]);

  return (
    <FormWrapper>
      <form className={style.form} onSubmit={handleSubmit(onSubmit)} noValidate>
        <Heading type="h1">
          {t(m.UpdateName.HeadingGreeting)}
          <br />
          {formTitle}
        </Heading>
        <p className={style.description}>
          {descriptionLines.map((line, index) => (
            <Fragment key={index}>
              {line}
              {index !== descriptionLines.length - 1 && <br />}
            </Fragment>
          ))}
        </p>
        <TextField
          {...register("name")}
          autoFocus
          placeholder={t(m.UpdateName.InputPlaceholder)}
          maxLength={MAX_NAME_LENGTH}
          errorMessage={errors.name?.message}
          error={Boolean(errors.name)}
          disabled={isPending || isAccountDataLoading}
          className={style.textField}
        />
        <Button
          type="submit"
          loading={isPending || isAccountDataLoading}
          disabled={userName === "" || isPending || isAccountDataLoading}
          className={style.submitButton}
        >
          {t(m.General.Continue)}
        </Button>
      </form>
    </FormWrapper>
  );
};
